// Revisado: 2025-12-24 [I18n Scan]
import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const SupportFilter = ({
    effects,
    selectedEffect,
    onEffectChange,
    selectedRarities,
    onRarityChange,
    selectedTypes,
    onTypeChange,
    uniqueEnabled,
    onUniqueToggle,
    selectedRegion,
    onRegionChange,
    loading,
    onlyInventory,
    onInventoryToggle,
    isGuest = false
}) => {
    const { t } = useTranslation();

    // --- Rarity Logic ---
    const rarities = ['SSR', 'SR', 'R'];
    const toggleRarity = (r) => {
        if (selectedRarities.includes(r)) {
            // Remove
            const newVal = selectedRarities.filter(x => x !== r);
            // If checking "Remove", effectively we show ALL if list is empty?
            // User: "Selecting all 3 at same time deselects automatically and shows all"
            onRarityChange(newVal);
        } else {
            // Add
            const newVal = [...selectedRarities, r];
            // If all 3 are selected, clear list (show all)
            if (newVal.length === 3) {
                onRarityChange([]);
            } else {
                onRarityChange(newVal);
            }
        }
    };
    const isRarityActive = (r) => selectedRarities.includes(r);

    // --- Type Logic ---
    const types = ['Speed', 'Stamina', 'Power', 'Guts', 'Wit', 'Pal', 'Group'];
    const toggleType = (t) => {
        if (selectedTypes.includes(t)) {
            onTypeChange(selectedTypes.filter(x => x !== t));
        } else {
            const newVal = [...selectedTypes, t];
            if (newVal.length === types.length) {
                onTypeChange([]);
            } else {
                onTypeChange(newVal);
            }
        }
    };
    const isTypeActive = (t) => selectedTypes.includes(t);

    // --- Effect Search Internal State ---
    const [searchTerm, setSearchTerm] = useState('');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
                // Reset search term to selected effect if user clicked away without selecting
                // Exception: if it was cleared (empty), keep it empty? User said "Si esta vacio se queda vacio" (If empty stay empty)
                // But also "si esta ya con un efecto se queda con el efecto" (if it had effect, keep it).

                // My logic:
                // If searchTerm is empty, it stays empty (and selection is likely empty too based on onChange logic).
                // If searchTerm has text but NOT the selected effect (abandoned edit), revert to selectedEffect.

                if (searchTerm !== '' && searchTerm !== selectedEffect) {
                    setSearchTerm(selectedEffect || '');
                    // NOTE: if selectedEffect is empty but searchTerm has garbage, this clears garbage. Correct.
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [searchTerm, selectedEffect]);

    // If user is searching (searchTerm exists) AND it's not just the already selected valid value being displayed
    // then filter. Otherwise, show ALL options so user can switch easily.
    const showAll = searchTerm === '' || searchTerm === selectedEffect;

    const filteredEffects = showAll
        ? effects
        : effects.filter(e => e.toLowerCase().includes(searchTerm.toLowerCase()));

    const handleSelectEffect = (e) => {
        onEffectChange(e);
        setSearchTerm(e);
        setIsDropdownOpen(false);
    };

    return (
        <div className="bg-white dark:bg-slate-800 p-6 rounded-xl border border-slate-200 dark:border-slate-700 shadow-xl space-y-6">

            {/* 1. Effect Selector (Mandatory) */}
            <div className="relative" ref={dropdownRef}>
                <label className="block text-slate-700 dark:text-cyan-400 font-bold mb-2 uppercase text-sm tracking-wider">
                    {t('SupportFilter.label_select_effect')} <span className="text-red-500 dark:text-red-400">*</span>
                </label>
                <div className="relative">
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setIsDropdownOpen(true);
                            if (e.target.value === '') onEffectChange('');
                        }}
                        onClick={() => setIsDropdownOpen(true)}
                        placeholder={t('SupportFilter.placeholder_search')}
                        className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg px-4 py-3 text-slate-900 dark:text-white focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 placeholder-slate-400 dark:placeholder-slate-500 transition-all font-mono"
                        disabled={loading}
                    />
                    {searchTerm && (
                        <button
                            onClick={() => { setSearchTerm(''); onEffectChange(''); }}
                            className="absolute right-3 top-3 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-white"
                        >
                            ✕
                        </button>
                    )}
                </div>

                {/* Dropdown Results */}
                {isDropdownOpen && filteredEffects.length > 0 && (
                    <ul className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg shadow-2xl max-h-60 overflow-y-auto custom-scrollbar">
                        {filteredEffects.map((eff) => (
                            <li
                                key={eff}
                                className={`px-4 py-2 cursor-pointer transition-colors ${selectedEffect === eff ? 'bg-cyan-100 dark:bg-cyan-900/50 text-cyan-700 dark:text-cyan-300' : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'}`}
                                onClick={() => handleSelectEffect(eff)}
                            >
                                {eff}
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* Region Selector */}
            <div>
                <label className="block text-slate-500 dark:text-slate-400 font-bold mb-2 uppercase text-xs tracking-wider">
                    {t('SupportFilter.label_region')}
                </label>
                <div className="flex bg-slate-100 dark:bg-slate-900 rounded-lg p-1 border border-slate-200 dark:border-slate-700 w-fit">
                    <button
                        onClick={() => onRegionChange('jp')}
                        className={`px-4 py-2 rounded-md font-bold text-sm transition-all ${selectedRegion === 'jp'
                            ? 'bg-white dark:bg-slate-700 text-slate-800 dark:text-white shadow-sm'
                            : 'text-slate-500 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                            }`}
                    >
                        {t('SupportFilter.btn_jp')}
                    </button>
                    <button
                        onClick={() => onRegionChange('global')}
                        className={`px-4 py-2 rounded-md font-bold text-sm transition-all ${selectedRegion === 'global'
                            ? 'bg-white dark:bg-slate-700 text-slate-800 dark:text-white shadow-sm'
                            : 'text-slate-500 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                            }`}
                    >
                        {t('SupportFilter.btn_global')}
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                {/* 2. Rarity Filter */}
                <div>
                    <label className="block text-slate-500 dark:text-slate-400 font-bold mb-2 uppercase text-xs tracking-wider">
                        {t('SupportFilter.label_rarity')}
                    </label>
                    <div className="flex flex-wrap gap-2">
                        {rarities.map(r => (
                            <button
                                key={r}
                                onClick={() => toggleRarity(r)}
                                className={`px-4 py-2 rounded-lg font-bold border transition-all ${isRarityActive(r)
                                    ? 'bg-gradient-to-br from-indigo-500 to-purple-600 border-transparent text-white shadow-lg shadow-indigo-500/30'
                                    : 'bg-slate-100 dark:bg-slate-900 border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-400 hover:border-slate-400 dark:hover:border-slate-500'
                                    }`}
                            >
                                {r}
                            </button>
                        ))}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                        {t('SupportFilter.help_rarity')}
                    </p>
                </div>

                {/* 3. Unique Bonus Toggle + Inventory Filter */}
                <div className="flex flex-col md:flex-row items-center justify-end gap-4">

                    {/* Only Inventory Toggle */}
                    <button
                        onClick={isGuest ? null : onInventoryToggle}
                        disabled={isGuest}
                        title={isGuest ? t('SupportFilter.tooltip_login_required') : ''}
                        className={`flex items-center gap-3 px-6 py-3 rounded-xl border transition-all w-full md:w-auto justify-center ${isGuest
                                ? 'bg-slate-200 dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-400 dark:text-slate-600 cursor-not-allowed grayscale'
                                : onlyInventory
                                    ? 'bg-purple-100 dark:bg-purple-900/30 border-purple-500/50 text-purple-700 dark:text-purple-300 shadow-lg shadow-purple-900/20'
                                    : 'bg-slate-100 dark:bg-slate-900 border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:border-slate-400 dark:hover:border-slate-500'
                            }`}
                    >
                        <div className={`w-5 h-5 rounded flex items-center justify-center border ${isGuest
                                ? 'border-slate-300 dark:border-slate-600 bg-slate-200 dark:bg-slate-800'
                                : onlyInventory
                                    ? 'bg-purple-500 border-purple-500 text-slate-900'
                                    : 'border-slate-400 dark:border-slate-500'
                            }`}>
                            {onlyInventory && !isGuest && <svg className="w-4 h-4 font-bold" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4"><path d="M5 12l5 5L20 7" /></svg>}
                        </div>
                        <span className="font-bold flex items-center gap-2">
                            <span>📦</span> {t('SupportFilter.btn_only_inventory') || "Only Inventory"}
                        </span>
                    </button>

                    {/* Unique Bonus Toggle */}
                    <button
                        onClick={onUniqueToggle}
                        className={`flex items-center gap-3 px-6 py-3 rounded-xl border transition-all w-full md:w-auto justify-center ${uniqueEnabled
                            ? 'bg-amber-100 dark:bg-amber-900/30 border-amber-500/50 text-amber-700 dark:text-amber-300 shadow-lg shadow-amber-900/20'
                            : 'bg-slate-100 dark:bg-slate-900 border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:border-slate-400 dark:hover:border-slate-500'
                            }`}
                    >
                        <div className={`w-5 h-5 rounded flex items-center justify-center border ${uniqueEnabled ? 'bg-amber-500 border-amber-500 text-slate-900' : 'border-slate-400 dark:border-slate-500'}`}>
                            {uniqueEnabled && <svg className="w-4 h-4 font-bold" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4"><path d="M5 12l5 5L20 7" /></svg>}
                        </div>
                        <span className="font-bold">{t('SupportFilter.btn_unique_bonus')}</span>
                    </button>
                </div>
            </div>

            {/* 4. Type Filter */}
            <div>
                <label className="block text-slate-500 dark:text-slate-400 font-bold mb-2 uppercase text-xs tracking-wider">
                    {t('SupportFilter.label_type')}
                </label>
                <div className="flex flex-wrap gap-2">
                    {types.map(t => (
                        <button
                            key={t}
                            onClick={() => toggleType(t)}
                            title={t}
                            className={`p-2 rounded-lg border transition-all flex items-center justify-center w-12 h-12 ${isTypeActive(t)
                                ? 'bg-cyan-100 dark:bg-cyan-900/50 border-cyan-500 shadow-lg shadow-cyan-500/20'
                                : 'bg-slate-100 dark:bg-slate-900 border-slate-300 dark:border-slate-600 hover:border-slate-400 dark:hover:border-slate-500 opacity-60 hover:opacity-100'
                                }`}
                        >
                            <img
                                src={['Speed', 'Stamina', 'Power', 'Guts', 'Wit'].includes(t)
                                    ? `/images/Icon/Stats_icons/Stat_Type_${t}.png`
                                    : `/images/Icon/Support_Icons/Support_Type_${t}.png`}
                                alt={t}
                                className="w-8 h-8 object-contain"
                                onError={(e) => { e.target.style.display = 'none'; }}
                            />
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default SupportFilter;
